import { CONSTANTS } from "./constants";

type LogLevel = "log" | "info" | "warn" | "error" | "debug";

interface LogOptions {
  prefix?: string;
  timestamp?: boolean;
  data?: any;
}

class Logger {
  private isEnabled: boolean;

  constructor() {
    // Convertir la valeur string en boolean
    this.isEnabled =
      CONSTANTS.SEE_LOGS === "true" || CONSTANTS.SEE_LOGS === "YES" || CONSTANTS.SEE_LOGS === "yes";
  }

  private formatMessage(
    level: LogLevel,
    message: any,
    options: LogOptions = {}
  ): string {
    const { prefix = "", timestamp = true, data = {} } = options;

    let formattedMessage = "";

    if (timestamp) {
      const now = new Date().toLocaleString("fr-FR");
      formattedMessage += `[${now}] `;
    }

    if (prefix) {
      formattedMessage += `[${prefix}] `;
    }

    if (data) {
      formattedMessage += `[${JSON.stringify(data)}] `;
    }

    formattedMessage += `[${level.toUpperCase()}] `;

    // Inclure le message dans le formatage
    if (typeof message === "string") {
      formattedMessage += message;
    } else {
      formattedMessage += JSON.stringify(message);
    }

    return formattedMessage;
  }

  log(message: any, options?: LogOptions): void {
    if (!this.isEnabled) return;

    const formattedMessage = this.formatMessage("log", message, options);
    console.log(formattedMessage);
  }

  info(message: any, options?: LogOptions): void {
    if (!this.isEnabled) return;

    const formattedMessage = this.formatMessage("info", message, options);
    console.info(formattedMessage);
  }

  warn(message: any, options?: LogOptions): void {
    if (!this.isEnabled) return;

    const formattedMessage = this.formatMessage("warn", message, options);
    console.warn(formattedMessage);
  }

  error(message: any, error?: Error, options?: LogOptions): void {
    if (!this.isEnabled) return;

    const formattedMessage = this.formatMessage("error", message, options);
    console.error(formattedMessage);

    if (error) {
      console.error("Stack trace:", error.stack);
    }
  }

  debug(message: any, options?: LogOptions): void {
    if (!this.isEnabled) return;

    const formattedMessage = this.formatMessage("debug", message, options);
    console.debug(formattedMessage);
  }

  // Méthodes utilitaires pour des logs spécialisés
  api(endpoint: string, method: string, data?: any): void {
    if (!this.isEnabled) return;

    this.info(`API Call: ${method} ${endpoint}`, { prefix: "API" });
    if (data) {
      this.debug(`Request Data:`, { prefix: "API" });
      console.log(data);
    }
  }

  apiResponse(endpoint: string, status: number, data?: any): void {
    if (!this.isEnabled) return;

    const message = `API Response: ${status} ${endpoint}`;
    const options = { prefix: "API" };

    if (status >= 400) {
      this.error(message, undefined, options);
    } else if (status >= 300) {
      this.warn(message, options);
    } else {
      this.info(message, options);
    }

    if (data) {
      this.debug(`Response Data:`, { prefix: "API" });
      console.log(data);
    }
  }

  performance(label: string, startTime: number): void {
    if (!this.isEnabled) return;

    const duration = performance.now() - startTime;
    this.info(`${label}: ${duration.toFixed(2)}ms`, { prefix: "PERF" });
  }

  // Méthode pour vérifier si les logs sont activés
  isLoggingEnabled(): boolean {
    return this.isEnabled;
  }
}

// Instance singleton du logger
const logger = new Logger();

export { logger, Logger };
export type { LogLevel, LogOptions };
